import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Box, Button, Card, Group, Text, TextInput } from '@grovus/design-system'
import type { ClientAccount } from '../data/account'
import { UNNAMED_CLIENT_KEY, foldName, type Client } from '../data/clients'
import { MEMBERS_COPY, REQUEST_COPY, type Dict, type Lang } from '../i18n'
import { fmt } from '../lib/format'
import { PanelHeading } from './PanelHeading'
import type { PanelProps } from './useDisclosurePanel'
import {
  CHECK_AGAIN_NOTICES, IDLE_CREATION, type CompanyCreation, type CreationBlock, type CreationNotice,
} from './useCompanyCreation'

interface Props {
  t: Dict
  clients: Client[]
  activeId: string
  onSelect: (id: string) => void
  onClose: () => void
  panelProps: PanelProps
  /** The add-client attempts and their handlers, owned by App (GRO-82). */
  creation?: CompanyCreation
  lang?: Lang
  /** Platform approval: "Add client" shows only with it (GRO-106). */
  canCreate?: boolean
  /** Received invitations; above zero, "N invitations waiting" opens the Invitations screen. */
  invitationCount?: number
  onOpenInvitations?: () => void
  /** Without the approval, "Request permission" opens the permission screen (GRO-107). */
  onRequestPermission?: () => void
}

/** A client's name, or the localized placeholder where first run stored an empty one. */
/**
 * The client's display name, with the demo marker when its figures are demo data.
 *
 * One function for both surfaces that name a client, the header's active client and the
 * switcher's rows, so the marker cannot appear on one and not the other. It is text
 * rather than colour, and because the switcher's row label is the radio's accessible
 * name, the marker reaches a screen reader with it.
 */
export function clientLabel(client: Client, t: Dict): string {
  const name = client.name === '' ? t[UNNAMED_CLIENT_KEY] : client.name
  return client.isDemo ? `${name} · ${t.demoMark}` : name
}

/**
 * An account as the switcher's row names it: the portfolio in parentheses, because ` · `
 * already separates the accounts on that line, and without the currency, which the narrow
 * line has no room for (GRO-109 design, D3).
 */
function accountLabel(account: ClientAccount, t: Dict): string {
  if (account.isDemo) return t.demoMarkLong
  return account.portfolioName ? `${account.name} (${account.portfolioName})` : account.name
}

/**
 * The company switcher's panel: the companies under this account and nothing else. The
 * account and its access moved to the user menu (GRO-66), so each header control does one
 * thing (design: docs/plans/2026-09-27-gro-66-header-user-menu-design.md).
 *
 * Telegram stays undrawn: GRO-12 and GRO-19 both deferred linking, and a dead control is
 * worse than no control.
 */
export function ClientPanel({
  t, clients, activeId, onSelect, onClose, panelProps, creation = IDLE_CREATION,
  lang = 'en', canCreate = true, invitationCount = 0, onOpenInvitations, onRequestPermission,
}: Props) {
  const { panel } = creation
  const copy = MEMBERS_COPY[lang]
  const canAdd = canCreate && panel.canAdd
  const offerAnyway = canCreate && panel.offerAnyway
  // The unsubmitted form belongs to the panel: nothing is at stake before a key exists, so
  // closing the panel discards the typed name, and the form's Cancel involves no handler.
  const [formOpen, setFormOpen] = useState(false)
  const [name, setName] = useState('')
  const [returnFocus, setReturnFocus] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  // The design system's Button takes no ref, so focus goes back through its wrapper.
  const addRef = useRef<HTMLDivElement>(null)
  const showForm = formOpen && (canAdd || offerAnyway)
  // The duplicate rule the backend applies (GRO-130, D6), checked here against the listed
  // clients so the common case needs no round trip; the backend's refusal covers the rest.
  // A name the backend refused counts as taken while the list may not show its company yet.
  const [refusedName, setRefusedName] = useState<string | null>(null)
  const folded = foldName(name)
  const duplicate = folded !== ''
    && (folded === refusedName || clients.some((client) => foldName(client.name) === folded))

  // A create the backend refused as a duplicate: the form comes back with its name.
  const refused = panel.duplicateName
  const onDuplicateShown = creation.onDuplicateShown
  useEffect(() => {
    if (refused === null) return
    setName(refused)
    setRefusedName(foldName(refused))
    setFormOpen(true)
    onDuplicateShown()
  }, [refused, onDuplicateShown])

  useEffect(() => {
    if (showForm) inputRef.current?.focus()
  }, [showForm])
  useEffect(() => {
    if (!returnFocus) return
    addRef.current?.querySelector('button')?.focus()
    setReturnFocus(false)
  }, [returnFocus])

  const openForm = () => {
    setName('')
    setFormOpen(true)
  }
  const cancelForm = () => {
    setFormOpen(false)
    setReturnFocus(true)
  }
  const submit = () => {
    const trimmed = name.trim()
    if (trimmed === '' || duplicate) return
    setFormOpen(false)
    creation.onCreate(trimmed)
  }

  return (
    <Card
      role="group"
      aria-label={t.clientPanelLabel}
      className="header-panel"
      style={{ left: 0, '--panel-width': '330px' } as CSSProperties}
      {...panelProps}
    >
      <Box>
        <PanelHeading>{t.clientLevel}</PanelHeading>
        {clients.map((client) => (
          <Box
            key={client.id}
            component="label"
            py="sm"
            style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', borderTop: '1px solid var(--mantine-color-default-border)' }}
          >
            <input
              type="radio"
              name="grovus-client"
              checked={client.id === activeId}
              onChange={() => {
                onSelect(client.id)
                onClose()
              }}
              style={{ marginTop: 3, accentColor: 'var(--mantine-color-brand-6)' }}
            />
            <Box style={{ flex: 1 }}>
              <Text fw={600} fz="sm">{clientLabel(client, t)}</Text>
              <Text fz={11} className="grv-muted" data-testid="client-role-line">
                {fmt(copy.roleLine, { role: copy.roles[client.role], n: client.accounts.length })}
              </Text>
              <Text fz={11} className="grv-muted">
                {client.accounts.length > 0
                  ? client.accounts.map((a) => accountLabel(a, t)).join(' · ')
                  : t.clientNone}
              </Text>
            </Box>
          </Box>
        ))}
        {panel.notices.length > 0 && (
          <Notices t={t} notices={panel.notices} onCheckAgain={creation.onCheckAgain} />
        )}
        {offerAnyway && !showForm && (
          <Box pt="sm">
            <Note>{t.clientAddInventoryRisk}</Note>
            <Actions>
              <Button
                variant="default"
                size="xs"
                onClick={() => {
                  creation.onAddAnyway()
                  openForm()
                }}
              >
                {t.clientAddAnyway}
              </Button>
            </Actions>
          </Box>
        )}
        {panel.blocks.map((attempt) => (
          <AttemptBlock
            key={attempt.key}
            t={t}
            attempt={attempt}
            onRetry={() => creation.onRetry(attempt.key)}
            onCancel={() => creation.onCancel(attempt.key)}
          />
        ))}
        {showForm && (
          <Box mt="sm" pt="sm" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
            <form
              onSubmit={(event) => {
                event.preventDefault()
                submit()
              }}
            >
              {/* An `xs` field with `sm` text, as the approved mock draws it (D7): Mantine's
                  `xs` would set the label and the typed name a size smaller. */}
              <TextInput
                ref={inputRef}
                size="xs"
                styles={{ label: { fontSize: 'var(--mantine-font-size-sm)' }, input: { fontSize: 'var(--mantine-font-size-sm)' } }}
                label={t.clientAddName}
                error={duplicate ? t.clientAddDuplicate : undefined}
                value={name}
                onChange={(event) => setName(event.currentTarget.value)}
              />
              <Actions>
                <Button type="submit" variant="filled" size="xs" disabled={name.trim() === '' || duplicate}>{t.clientAddCreate}</Button>
                <Button variant="default" size="xs" onClick={cancelForm}>{t.clientAddCancel}</Button>
              </Actions>
            </form>
          </Box>
        )}
        {invitationCount > 0 && onOpenInvitations && (
          <Box pt="sm">
            <Button
              variant="subtle"
              size="xs"
              px={0}
              onClick={() => {
                onOpenInvitations()
                onClose()
              }}
            >
              {fmt(copy.waiting, { n: invitationCount })}
            </Button>
          </Box>
        )}
        {canAdd && !showForm && (
          <Box ref={addRef} pt="sm">
            <Button variant="default" size="xs" onClick={openForm}>{t.clientAdd}</Button>
          </Box>
        )}
        {!canCreate && (
          <Box pt="sm" data-testid="client-add-locked">
            <Button variant="default" size="xs" disabled>{t.clientAdd}</Button>
            <Note>{REQUEST_COPY[lang].permLede}</Note>
            {onRequestPermission && (
              <Button
                mt={6}
                variant="subtle"
                size="xs"
                onClick={() => {
                  onRequestPermission()
                  onClose()
                }}
              >
                {REQUEST_COPY[lang].permSubmit}
              </Button>
            )}
          </Box>
        )}
        <Text fz={11} pt="sm" className="grv-muted" style={{ lineHeight: 1.5 }}>{t.clientSwitchNote}</Text>
      </Box>
    </Card>
  )
}

/** The panel's muted qualifying line, `11px` as the mock draws it. */
function Note({ children, testId }: { children: ReactNode; testId?: string }) {
  return <Text fz={11} pt={6} className="grv-muted" style={{ lineHeight: 1.5 }} data-testid={testId}>{children}</Text>
}

function Actions({ children }: { children: ReactNode }) {
  return <Group gap="sm" pt="sm">{children}</Group>
}

const NOTICE_KEY: Record<CreationNotice, keyof Dict> = {
  unavailable: 'clientAddUnavailable',
  notListed: 'clientAddNotListed',
  refreshFailed: 'clientAddRefreshFailed',
  checkFailedCreated: 'clientAddCheckFailedCreated',
  alreadyCreated: 'clientAddAlreadyCreated',
  cancelled: 'clientAddCancelled',
  inventoryFailed: 'clientAddInventoryFailed',
  keepOpen: 'clientAddKeepOpen',
}

/** One status region for every notice, and one "Check again" for those it answers. */
function Notices({ t, notices, onCheckAgain }: { t: Dict; notices: CreationNotice[]; onCheckAgain: () => void }) {
  const checkAgain = notices.some((notice) => CHECK_AGAIN_NOTICES.includes(notice))
  return (
    <Box pt="sm">
      <Box role="status">
        {notices.map((notice) => (
          <Text key={notice} fz="sm" pt={6} style={{ lineHeight: 1.5 }}>{t[NOTICE_KEY[notice]] as string}</Text>
        ))}
      </Box>
      {checkAgain && (
        <Actions>
          <Button variant="default" size="xs" onClick={onCheckAgain}>{t.clientAddCheckAgain}</Button>
        </Actions>
      )}
    </Box>
  )
}

/**
 * One attempt, under the radios behind the rows' hairline. The unresolved line is body text
 * in the default colour, not the `bad` ramp: nothing has failed for certain (D7).
 */
function AttemptBlock({ t, attempt, onRetry, onCancel }: {
  t: Dict
  attempt: CreationBlock
  onRetry: () => void
  onCancel: () => void
}) {
  return (
    <Box mt="sm" pt="sm" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
      <Text fw={600} fz="sm">{attempt.name}</Text>
      {attempt.state === 'unresolved' && (
        <Text role="alert" fz="sm" pt={6} style={{ lineHeight: 1.5 }}>
          {attempt.oldBackend ? `${t.clientAddUnknown} ${t.clientAddUnknownOld}` : t.clientAddUnknown}
        </Text>
      )}
      {attempt.state === 'pendingHere' && <Note>{t.clientAddPending}</Note>}
      {attempt.state === 'elsewhere' && <Note>{t.clientAddElsewhere}</Note>}
      {attempt.checkFailed && <Note>{t.clientAddCheckFailed}</Note>}
      {attempt.keepOpen && <Note>{t.clientAddKeepOpen}</Note>}
      <Actions>
        {attempt.state === 'pendingHere' && (
          <Button variant="filled" size="xs" loading>{t.clientAddCreate}</Button>
        )}
        {attempt.state === 'unresolved' && (
          <Button variant="filled" size="xs" onClick={onRetry}>{t.retry}</Button>
        )}
        <Button variant="default" size="xs" onClick={onCancel}>{t.clientAddCancel}</Button>
      </Actions>
    </Box>
  )
}
